import { Outlet, createFileRoute } from '@tanstack/react-router'
import { Globe } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BrandMark } from '@/components/brand-mark'
import i18n, { LANG_STORAGE_KEY, SUPPORTED_LANGS } from '@/i18n'

const WWW_URL = import.meta.env.VITE_SITE_URL ?? 'https://goaltree.ai'

export const Route = createFileRoute('/auth')({
  component: AuthLayout,
})

function CheckIcon() {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3 text-primary-text" fill="none" aria-hidden="true">
      <path
        d="M2 6l3 3 5-5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type Slide = { tag: string; headline: string; desc: string }

const SLIDE_DURATION = 5000

function SlideVisual({ index }: { index: number }) {
  if (index === 0) {
    return (
      <div className="flex flex-col text-xs">
        {/* M1 row */}
        <div className="flex items-center gap-2 py-1.5">
          <span className="flex h-5 w-6 shrink-0 items-center justify-center rounded-md bg-primary text-[9px] font-black text-black">
            M1
          </span>
          <span className="flex-1 font-semibold text-white/80">Build core fitness</span>
          <span className="text-[10px] text-white/30">1/4 tasks</span>
        </div>
        {/* T1 row */}
        <div className="flex items-center gap-2 py-1 pl-2 border-l border-primary/30 ml-3">
          <span className="flex h-4 w-5 shrink-0 items-center justify-center rounded text-[8px] font-bold text-primary-text border border-primary/40 bg-primary/10">
            T1
          </span>
          <span className="flex-1 text-white/50">Weekly runs</span>
          <span className="text-[10px] text-white/25">1/3 tasks</span>
        </div>
        {/* Tasks */}
        <div className="flex flex-col pl-2 border-l border-primary/30 ml-3">
          <div className="flex items-center gap-2 py-1 pl-2">
            <div className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-primary/20 border border-primary/60">
              <svg viewBox="0 0 8 8" className="h-2 w-2" fill="none">
                <path
                  d="M1.5 4l2 2 3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary-text"
                />
              </svg>
            </div>
            <span className="flex-1 text-white/30 line-through">Run 5k without stopping</span>
          </div>
          <div className="flex items-center gap-2 py-1 pl-2">
            <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-white/25" />
            <span className="flex-1 text-white/55">Track pace twice a week</span>
          </div>
          <div className="flex items-center gap-2 py-1 pl-2">
            <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-white/25" />
            <span className="flex-1 text-white/55">Rest and stretch routine</span>
          </div>
        </div>
        {/* M2 row */}
        <div className="flex items-center gap-2 py-1.5 mt-1">
          <span className="flex h-5 w-6 shrink-0 items-center justify-center rounded-md bg-primary text-[9px] font-black text-black">
            M2
          </span>
          <span className="flex-1 font-semibold text-white/80">Improve nutrition</span>
          <span className="text-[10px] text-white/30">0/4 tasks</span>
        </div>
        {/* M3 row */}
        <div className="flex items-center gap-2 py-1.5">
          <span className="flex h-5 w-6 shrink-0 items-center justify-center rounded-md bg-primary text-[9px] font-black text-black">
            M3
          </span>
          <span className="flex-1 font-semibold text-white/80">Build consistency habit</span>
          <span className="text-[10px] text-white/30">0/4 tasks</span>
        </div>
      </div>
    )
  }
  if (index === 1) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-xs text-white/40">
          "I want to run a marathon in 6 months"
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-primary-text/70 px-1">
          <span className="inline-block h-1 w-1 rounded-full bg-primary animate-pulse" />
          Generating your plan…
        </div>
        <div className="flex flex-col gap-2">
          {['Build base endurance', 'Increase weekly mileage', 'Race-day preparation'].map(
            (item, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 rounded-lg border border-white/8 bg-white/4 px-3 py-2"
              >
                <div className="h-2 w-2 rounded-full border border-primary/50 bg-primary/20 shrink-0" />
                <span className="text-xs text-white/50">{item}</span>
              </div>
            ),
          )}
        </div>
      </div>
    )
  }
  if (index === 2) {
    return (
      <div className="flex flex-col gap-2">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-primary-text/60">
          Today
        </div>
        {[
          { label: 'Focus', title: 'Draft project proposal', done: true },
          { label: 'Focus', title: 'Review pull requests', done: false },
          { label: 'Quick', title: 'Send weekly update', done: false },
        ].map((task, i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/4 px-3 py-2.5"
          >
            <div
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${task.done ? 'border-primary bg-primary/30' : 'border-white/20'}`}
            >
              {task.done && <CheckIcon />}
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={`text-xs ${task.done ? 'line-through text-white/25' : 'text-white/60'}`}
              >
                {task.title}
              </span>
            </div>
            <span className="text-[9px] font-semibold uppercase tracking-wide text-primary-text/50">
              {task.label}
            </span>
          </div>
        ))}
      </div>
    )
  }
  if (index === 3) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="flex gap-2.5 items-start">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary-text">
            C
          </div>
          <div className="rounded-xl rounded-tl-none border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/50 max-w-[220px]">
            What's the biggest thing holding you back right now?
          </div>
        </div>
        <div className="flex gap-2.5 items-start justify-end">
          <div className="rounded-xl rounded-tr-none border border-primary/20 bg-primary/10 px-3 py-2 text-xs text-white/60 max-w-[200px]">
            I keep starting goals but losing momentum after a few weeks.
          </div>
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold text-white/40">
            Y
          </div>
        </div>
        <div className="flex gap-2.5 items-start">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-bold text-primary-text">
            C
          </div>
          <div className="rounded-xl rounded-tl-none border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/50 max-w-[220px]">
            Let's break your goal into smaller wins. Which area matters most this month?
          </div>
        </div>
      </div>
    )
  }
  // index === 4 — Voice Capture
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 rounded-xl border border-white/8 bg-white/4 px-3 py-2.5">
        <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse shrink-0" />
        <span className="text-xs text-white/40 italic leading-relaxed">
          "I need to finish the pitch deck, book flights for the conference, and start learning
          Spanish…"
        </span>
      </div>
      <div className="flex items-center gap-1.5 px-1 text-[10px] font-semibold uppercase tracking-widest text-primary-text/60">
        <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none">
          <path
            d="M2 6l3 3 5-5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        3 goals extracted
      </div>
      <div className="flex flex-col gap-1.5">
        {['Finish the pitch deck', 'Book flights for the conference', 'Start learning Spanish'].map(
          (goal, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 rounded-lg border border-primary/15 bg-primary/8 px-3 py-2"
            >
              <div className="h-2 w-2 rounded-full border border-primary/50 bg-primary/20 shrink-0" />
              <span className="text-xs text-white/60">{goal}</span>
            </div>
          ),
        )}
      </div>
    </div>
  )
}

function BrandPanel() {
  const { t } = useTranslation()
  const slides = t('authSlides', { returnObjects: true }) as Slide[]
  const [active, setActive] = useState(0)
  const [progress, setProgress] = useState(0)
  const [paused, setPaused] = useState(false)
  const [transitioning, setTransitioning] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function goTo(idx: number) {
    if (idx === active) return
    setTransitioning(true)
    setTimeout(() => {
      setActive(idx)
      setProgress(0)
      setTransitioning(false)
    }, 300)
  }

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (progressRef.current) clearInterval(progressRef.current)
      return
    }
    setProgress(0)
    const tick = 50
    progressRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + (tick / SLIDE_DURATION) * 100, 100))
    }, tick)
    intervalRef.current = setInterval(() => {
      setTransitioning(true)
      setTimeout(() => {
        setActive((a) => (a + 1) % slides.length)
        setProgress(0)
        setTransitioning(false)
      }, 300)
    }, SLIDE_DURATION)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (progressRef.current) clearInterval(progressRef.current)
    }
  }, [active, paused, slides.length])

  const slide = slides[active]

  return (
    <div
      className="relative hidden overflow-hidden bg-[#0b0f02] lg:flex lg:flex-col lg:justify-between p-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Glow layers */}
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-primary/[0.18] blur-[130px]" />
      <div className="pointer-events-none absolute top-1/3 -left-20 h-72 w-72 rounded-full bg-primary/[0.08] blur-[90px]" />

      {/* Logo */}
      <a href={WWW_URL} className="relative flex w-fit items-center gap-2.5">
        <BrandMark />
        <span className="font-logo text-xl font-extrabold tracking-tight text-white">Goaltree</span>
      </a>

      {/* Slide content */}
      <div
        className="relative space-y-8 transition-all duration-300"
        style={{
          opacity: transitioning ? 0 : 1,
          transform: transitioning ? 'translateY(8px)' : 'translateY(0)',
        }}
      >
        {/* Visual mock */}
        <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-5 backdrop-blur-sm">
          <SlideVisual index={active} />
        </div>

        {/* Tag */}
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary-text">
            {slide?.tag}
          </span>
        </div>

        {/* Headline + desc */}
        <div className="space-y-3">
          <h2 className="text-[2rem] font-black leading-[1.1] tracking-tight text-white">
            {slide?.headline}
          </h2>
          <p className="max-w-[300px] text-sm leading-relaxed text-white/50">{slide?.desc}</p>
        </div>

        {/* Dot indicators + progress */}
        <div className="flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className="relative h-1 rounded-full overflow-hidden cursor-pointer transition-all duration-300"
              style={{ width: i === active ? 32 : 16, background: 'rgba(255,255,255,0.15)' }}
            >
              {i === active && (
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-primary transition-none"
                  style={{ width: `${progress}%` }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      <p className="relative text-xs text-white/25">
        © {new Date().getFullYear()} Goaltree. {t('login.copyright')}
      </p>
    </div>
  )
}

const LANG_LABELS: Record<string, string> = {
  en: 'English',
  ja: '日本語',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  zh: '中文',
}

function LangSelector() {
  const { i18n: i18nInstance } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const currentLang = i18nInstance.language?.split('-')[0] ?? 'en'

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Globe className="h-3.5 w-3.5" />
        {LANG_LABELS[currentLang] ?? currentLang}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[120px] rounded-lg border border-border bg-popover p-1 shadow-md">
          {SUPPORTED_LANGS.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                i18n.changeLanguage(lang)
                localStorage.setItem(LANG_STORAGE_KEY, lang)
                setOpen(false)
              }}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs transition-colors hover:bg-muted ${currentLang === lang ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
            >
              {currentLang === lang && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
              {currentLang !== lang && <span className="h-1.5 w-1.5" />}
              {LANG_LABELS[lang]}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function AuthLayout() {
  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      {/* Form column */}
      <div className="relative flex flex-col items-center justify-center overflow-hidden px-6 pt-20 pb-8 lg:p-8">
        {/* Ambient accent spotlight — two soft orbs that gently breathe/drift. */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute right-[-12%] top-[-18%] h-[460px] w-[460px] rounded-full bg-primary/[0.16] blur-[120px]"
            style={{ animation: 'authGlowA 6s ease-in-out infinite alternate' }}
          />
          <div
            className="absolute bottom-[-18%] left-[-12%] h-[420px] w-[420px] rounded-full bg-primary/[0.10] blur-[120px]"
            style={{ animation: 'authGlowB 7.5s ease-in-out infinite alternate' }}
          />
        </div>
        <style>{`@keyframes authGlowA{from{transform:translateY(0) scale(1);opacity:.75}to{transform:translateY(26px) scale(1.08);opacity:1}}@keyframes authGlowB{from{transform:translateY(0) scale(1.04);opacity:.65}to{transform:translateY(-22px) scale(1);opacity:.9}}`}</style>
        {/* Header row — mobile logo (hidden on lg, where the brand panel shows it)
            + language selector, vertically centered so they align. */}
        <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-4 px-6 py-6 lg:justify-end">
          <a href={WWW_URL} className="flex items-center gap-2.5 lg:hidden">
            <BrandMark />
            <span className="font-logo text-xl font-extrabold tracking-tight text-foreground">
              Goaltree
            </span>
          </a>
          <LangSelector />
        </div>
        <div className="relative z-10 w-full max-w-sm">
          <Outlet />
        </div>
      </div>

      {/* Brand column — desktop only */}
      <BrandPanel />
    </div>
  )
}
